import * as Yup from 'yup';
import { PaymentMethodType } from '../../../../models/subscription';

/**
 * Sprint 28, story-144 (residual gap). Validation schema for PaymentMethodSelector, built as a
 * factory that takes the caller's own translated `paymentMethod` catalog section — this file has
 * no component to call `useTranslation` from (Decision 2: non-component `.ts` utilities take
 * their specifics as arguments, never call the hook themselves). Both consumers
 * (`PaymentMethodSelector/index.tsx` and `PaymentMethodForm.tsx`) call this with their own
 * `useTranslation('paymentMethod')` result.
 */
export const buildPaymentMethodValidationSchema = (t: Record<string, string>) =>
  Yup.object({
    paymentMethodType: Yup.string().required(t.selectPaymentMethodRequired),

    // Billing Information
    billingFirstName: Yup.string().required(t.firstNameRequired),

    billingLastName: Yup.string().required(t.lastNameRequired),

    billingEmail: Yup.string().email(t.invalidEmail).required(t.emailRequired),

    billingAddress1: Yup.string().required(t.addressRequired),

    billingCity: Yup.string().required(t.cityRequired),

    billingState: Yup.string()
      .matches(/^[A-Z]{2}$/, t.stateFormat)
      .required(t.stateRequired),

    billingZip: Yup.string().required(t.zipRequired),

    billingPhone: Yup.string().when('paymentMethodType', {
      is: 'ach',
      then: (schema) =>
        schema
          .matches(/^[\d\s\-+()]+$/, t.phoneFormat)
          .min(10, t.phoneMinLength)
          .required(t.phoneRequiredAch),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Credit Card Fields - validated when payment method is CREDIT_CARD
    ccNumber: Yup.string().when('paymentMethodType', {
      is: PaymentMethodType.CREDIT_CARD,
      then: (schema) =>
        schema
          .required(t.cardNumberRequired)
          .test('cardNumber', t.invalidCardNumber, (value) => {
            if (!value) return false;
            const digits = value.replace(/\s/g, '');
            return /^\d{13,19}$/.test(digits);
          }),
      otherwise: (schema) => schema.notRequired(),
    }),

    ccExp: Yup.string().when('paymentMethodType', {
      is: PaymentMethodType.CREDIT_CARD,
      then: (schema) =>
        schema
          .required(t.expirationRequired)
          .matches(/^\d{2}\/\d{2}$/, t.expirationFormat)
          .test('expiration', t.cardExpired, (value) => {
            if (!value) return false;
            // Skip expiration validation in development mode
            if (process.env.NODE_ENV === 'development') {
              return true;
            }
            const [month, year] = value.split('/').map(Number);
            const expDate = new Date(2000 + year, month - 1);
            const now = new Date();
            return expDate > now;
          }),
      otherwise: (schema) => schema.notRequired(),
    }),

    cvv: Yup.string().when('paymentMethodType', {
      is: PaymentMethodType.CREDIT_CARD,
      then: (schema) =>
        schema.required(t.cvvRequired).matches(/^\d{3,4}$/, t.cvvFormat),
      otherwise: (schema) => schema.notRequired(),
    }),

    // ACH Fields
    checkName: Yup.string().when('paymentMethodType', {
      is: 'ach',
      then: (schema) => schema.required(t.accountHolderNameRequired),
      otherwise: (schema) => schema.notRequired(),
    }),

    checkAba: Yup.string().when('paymentMethodType', {
      is: 'ach',
      then: (schema) =>
        schema.matches(/^\d{9}$/, t.routingNumberFormat).required(t.routingNumberRequired),
      otherwise: (schema) => schema.notRequired(),
    }),

    checkAccount: Yup.string().when('paymentMethodType', {
      is: 'ach',
      then: (schema) => schema.required(t.accountNumberRequired),
      otherwise: (schema) => schema.notRequired(),
    }),

    accountType: Yup.string().when('paymentMethodType', {
      is: 'ach',
      then: (schema) => schema.oneOf(['checking', 'savings']).required(t.accountTypeRequired),
      otherwise: (schema) => schema.notRequired(),
    }),

    accountHolderType: Yup.string().when('paymentMethodType', {
      is: 'ach',
      then: (schema) =>
        schema.oneOf(['personal', 'business']).required(t.accountHolderTypeRequired),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

/**
 * Initial values for PaymentMethodSelector component
 * Use this when initializing Formik in other parts of the application
 */
export const paymentMethodInitialValues = {
  paymentMethodType: PaymentMethodType.CREDIT_CARD,
  billingFirstName: '',
  billingLastName: '',
  billingEmail: '',
  billingAddress1: '',
  billingAddress2: '',
  billingCity: '',
  billingState: '',
  billingZip: '',
  billingCountry: 'US',
  billingPhone: '',
  ccNumber: '',
  ccExp: '',
  cvv: '',
  checkName: '',
  checkAba: '',
  checkAccount: '',
  accountType: 'checking',
  accountHolderType: 'personal',
};
